/**
 * Time constants for Xendit invoice duration calculations
 */
const SECONDS_PER_HOUR = 3600 // 60 * 60
const SECONDS_PER_DAY = 86400 // 60 * 60 * 24

/**
 * Converts time duration to invoice duration (in seconds).
 * Use this when you want the invoice to expire X hours/days **from now**.
 *
 * @param duration - Duration object with either hours or days
 * @returns Duration in seconds
 *
 * @example
 * ```typescript
 * // Invoice expires in 24 hours from now
 * const duration = invoiceDuration({ hours: 24 }) // 86400
 *
 * // Invoice expires in 7 days from now
 * const duration = invoiceDuration({ days: 7 }) // 604800
 *
 * // Common use case: NEW membership
 * const payload = createXenditInvoicePayload({
 *   invoiceDuration: invoiceDuration({ hours: 48 }),
 *   // ... other fields
 * })
 * ```
 */
export function invoiceDuration(duration: { hours: number } | { days: number }): number {
  if ('hours' in duration) {
    return duration.hours * SECONDS_PER_HOUR
  }
  return duration.days * SECONDS_PER_DAY
}

/**
 * Calculates invoice duration based on a target expiry date.
 * Use this when you have a **specific end date** (like membership expiry) and want
 * the invoice to expire X hours/days **before** that date.
 *
 * @param params - Configuration for calculating duration
 * @param params.expiryDate - The target expiry date (e.g., membership end date)
 * @param params.before - How much time before the expiry date the invoice should expire (hours or days)
 * @returns Duration in seconds
 *
 * @example
 * ```typescript
 * // Membership ends on Dec 31, 2025 at midnight
 * // Invoice should expire 2 hours before that (Dec 30 at 10 PM)
 * const membershipEndDate = new Date('2025-12-31T00:00:00Z')
 * const duration = invoiceDurationFromExpiryDate({
 *   expiryDate: membershipEndDate,
 *   before: { hours: 2 }
 * }) // Returns seconds from now until Dec 30 at 10 PM
 *
 * // Invoice expires 1 day before membership ends
 * const duration = invoiceDurationFromExpiryDate({
 *   expiryDate: membershipEndDate,
 *   before: { days: 1 }
 * }) // Returns seconds from now until Dec 30 at midnight
 *
 * // For ADD-ON seats: expire 1 hour before membership ends
 * const duration = invoiceDurationFromExpiryDate({
 *   expiryDate: membership.endDate,
 *   before: { hours: 1 }
 * })
 * ```
 */
export function invoiceDurationFromExpiryDate({
  expiryDate,
  before = { hours: 1 },
}: {
  expiryDate: Date
  before?: { hours: number } | { days: number }
}): number {
  const now = new Date()

  // Calculate offset in milliseconds
  const offsetMs = 'hours' in before ? before.hours * SECONDS_PER_HOUR * 1000 : before.days * SECONDS_PER_DAY * 1000

  const targetExpiryTime = new Date(expiryDate.getTime() - offsetMs)
  const differenceInMs = targetExpiryTime.getTime() - now.getTime()
  const differenceInSeconds = Math.floor(differenceInMs / 1000)

  // Return positive duration, or 0 if already passed
  return Math.max(0, differenceInSeconds)
}
